import React from "react";
import PropTypes from "prop-types";
import { Clock3, X } from "lucide-react";

export const SLOT_MINUTES = 30;
const TOTAL_MINUTES = 24 * 60;
const SLOT_COUNT = TOTAL_MINUTES / SLOT_MINUTES;
const HOUR_MARKERS = Array.from({ length: 12 }, (_, index) => index * 2);
const SLOT_DEFINITIONS = Array.from({ length: SLOT_COUNT }, (_, index) => {
    const startMinutes = index * SLOT_MINUTES;
    const endMinutes = startMinutes + SLOT_MINUTES;

    return {
        index,
        startLabel: formatMinutesAsTime(startMinutes),
        endLabel: formatMinutesAsTime(endMinutes),
    };
});

function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}

export function formatMinutesAsTime(totalMinutes) {
    const safeMinutes = clamp(totalMinutes, 0, TOTAL_MINUTES);
    const hours = Math.floor(safeMinutes / 60);
    const minutes = safeMinutes % 60;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

export function parseTimeToMinutes(timeValue) {
    if (typeof timeValue !== "string" || !timeValue.includes(":")) return null;

    const [hoursText, minutesText] = timeValue.split(":");
    const hours = Number(hoursText);
    const minutes = Number(minutesText);

    if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return null;
    if (minutes < 0 || minutes >= 60) return null;
    if (hours < 0 || hours > 24) return null;
    if (hours === 24 && minutes !== 0) return null;

    return (hours * 60) + minutes;
}

export function buildRangeFromTimes(startTime, endTime) {
    const startMinutes = parseTimeToMinutes(startTime);
    const endMinutes = parseTimeToMinutes(endTime);

    if (startMinutes == null || endMinutes == null || endMinutes <= startMinutes) {
        return null;
    }

    return {
        startIndex: clamp(Math.floor(startMinutes / SLOT_MINUTES), 0, SLOT_COUNT - 1),
        endIndex: clamp(Math.ceil(endMinutes / SLOT_MINUTES) - 1, 0, SLOT_COUNT - 1),
        startTime,
        endTime,
    };
}

export function buildRangeFromSlotIndexes(anchorIndex, currentIndex) {
    const startIndex = clamp(Math.min(anchorIndex, currentIndex), 0, SLOT_COUNT - 1);
    const endIndex = clamp(Math.max(anchorIndex, currentIndex), 0, SLOT_COUNT - 1);

    return {
        startIndex,
        endIndex,
        startTime: formatMinutesAsTime(startIndex * SLOT_MINUTES),
        endTime: formatMinutesAsTime((endIndex + 1) * SLOT_MINUTES),
    };
}

function getSlotIndexFromPoint(eventTargetPoint) {
    const element = document.elementFromPoint(eventTargetPoint.clientX, eventTargetPoint.clientY);
    const slotButton = element?.closest?.("[data-slot-index]");

    if (!slotButton) return null;

    const slotIndex = Number(slotButton.dataset.slotIndex);
    return Number.isInteger(slotIndex) ? slotIndex : null;
}

function WeekTimeRangePicker({ startTime = "", endTime = "", onChange }) {
    const [isOpen, setIsOpen] = React.useState(false);
    const [dragState, setDragState] = React.useState(null);

    const selectedRange = React.useMemo(
        () => buildRangeFromTimes(startTime, endTime),
        [startTime, endTime],
    );

    const previewRange = dragState
        ? buildRangeFromSlotIndexes(dragState.anchorIndex, dragState.currentIndex)
        : selectedRange;

    const selectedLabel = selectedRange ? `${selectedRange.startTime} – ${selectedRange.endTime}` : "Select a time block";

    const handleSelectionCommit = React.useCallback((range) => {
        onChange(range.startTime, range.endTime);
        setDragState(null);
        setIsOpen(false);
    }, [onChange]);

    const beginSelection = React.useCallback((slotIndex) => {
        setDragState({ anchorIndex: slotIndex, currentIndex: slotIndex });
    }, []);

    const extendSelection = React.useCallback((slotIndex) => {
        setDragState((prev) => {
            if (!prev) return prev;
            return { ...prev, currentIndex: slotIndex };
        });
    }, []);

    const finishSelection = React.useCallback((slotIndex = null) => {
        if (!dragState) return;

        const resolvedRange = buildRangeFromSlotIndexes(dragState.anchorIndex, slotIndex ?? dragState.currentIndex);
        handleSelectionCommit(resolvedRange);
    }, [dragState, handleSelectionCommit]);

    React.useEffect(() => {
        if (!dragState) return undefined;

        if (typeof window === "undefined") {
            return undefined;
        }

        const handleMouseUp = () => finishSelection();
        const handleTouchEnd = (event) => {
            const touchPoint = event.changedTouches?.[0];
            const slotIndex = touchPoint ? getSlotIndexFromPoint(touchPoint) : null;
            finishSelection(slotIndex);
        };

        window.addEventListener("mouseup", handleMouseUp);
        window.addEventListener("touchend", handleTouchEnd);
        window.addEventListener("touchcancel", handleMouseUp);

        return () => {
            window.removeEventListener("mouseup", handleMouseUp);
            window.removeEventListener("touchend", handleTouchEnd);
            window.removeEventListener("touchcancel", handleMouseUp);
        };
    }, [dragState, finishSelection]);

    const handleTouchMove = React.useCallback((event) => {
        if (!dragState) return;

        const touchPoint = event.touches?.[0];
        if (!touchPoint) return;

        const slotIndex = getSlotIndexFromPoint(touchPoint);
        if (slotIndex == null) return;

        event.preventDefault();
        extendSelection(slotIndex);
    }, [dragState, extendSelection]);

    const clearSelection = React.useCallback(() => {
        onChange("", "");
        setDragState(null);
        setIsOpen(false);
    }, [onChange]);

    return (
        <div className='space-y-2'>
            <div className='flex flex-wrap items-center gap-2'>
                <button
                    type='button'
                    onClick={() => setIsOpen((prev) => !prev)}
                    className='inline-flex min-w-0 flex-1 items-center gap-2 rounded-md border border-[#3B435A] bg-[#23293A] px-3 py-2 text-left text-xs text-gray-200 transition hover:bg-[#2A3144] focus:outline-none focus:ring-2 focus:ring-indigo-500'
                    aria-expanded={isOpen}
                >
                    <Clock3 size={14} className='shrink-0 text-indigo-300' />
                    <span className='truncate'>{selectedLabel}</span>
                </button>

                {selectedRange && (
                    <button
                        type='button'
                        onClick={clearSelection}
                        className='inline-flex items-center gap-1 rounded-md border border-[#3B435A] bg-[#1F2535] px-2.5 py-2 text-[11px] font-medium text-gray-300 transition hover:border-red-400 hover:text-red-300'
                    >
                        <X size={12} />
                        Clear
                    </button>
                )}
            </div>

            {isOpen && (
                <div className='rounded-lg border border-[#2A3144] bg-[#171C2A] p-3 shadow-lg'>
                    <div className='mb-3 flex flex-wrap items-start justify-between gap-3'>
                        <div>
                            <p className='text-xs font-semibold text-gray-200'>Drag across the day</p>
                            <p className='text-[11px] text-gray-400'>24-hour timeline · 30 minute blocks</p>
                        </div>
                        <div className='rounded-full border border-[#30384D] bg-[#1F2535] px-2.5 py-1 text-[11px] font-medium text-indigo-200'>
                            {previewRange ? `${previewRange.startTime} – ${previewRange.endTime}` : "Optional"}
                        </div>
                    </div>

                    <div className='overflow-x-auto pb-2' onTouchMove={handleTouchMove}>
                        <div className='min-w-[720px] select-none'>
                            <div className='mb-2 grid grid-cols-12 text-[10px] text-gray-500'>
                                {HOUR_MARKERS.map((hour) => (
                                    <span key={hour}>{String(hour).padStart(2, "0")}:00</span>
                                ))}
                            </div>

                            <div className='flex gap-1'>
                                {SLOT_DEFINITIONS.map((slot) => {
                                    const isSelected = previewRange
                                        && slot.index >= previewRange.startIndex
                                        && slot.index <= previewRange.endIndex;
                                    const isRangeStart = slot.index === previewRange?.startIndex;
                                    const isRangeEnd = slot.index === previewRange?.endIndex;

                                    return (
                                        <button
                                            key={slot.index}
                                            type='button'
                                            data-slot-index={slot.index}
                                            aria-label={`Select ${slot.startLabel} to ${slot.endLabel}`}
                                            onMouseDown={(event) => {
                                                event.preventDefault();
                                                beginSelection(slot.index);
                                            }}
                                            onMouseEnter={() => extendSelection(slot.index)}
                                            onMouseUp={() => finishSelection(slot.index)}
                                            onTouchStart={(event) => {
                                                event.preventDefault();
                                                beginSelection(slot.index);
                                            }}
                                            className={`h-12 min-w-[14px] flex-1 border transition ${isSelected
                                                ? "border-indigo-300 bg-indigo-500/40"
                                                : "border-[#30384D] bg-[#23293A] hover:bg-[#2A3144]"
                                            } ${isRangeStart ? "rounded-l-md" : "rounded-none"} ${isRangeEnd ? "rounded-r-md" : "rounded-none"}`}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    <div className='mt-3 flex flex-wrap items-center justify-between gap-2'>
                        <p className='text-[11px] text-gray-500'>Leave it empty if this item does not need a specific time range.</p>
                        <button
                            type='button'
                            onClick={() => {
                                setDragState(null);
                                setIsOpen(false);
                            }}
                            className='rounded-md border border-[#30384D] px-2.5 py-1.5 text-[11px] font-medium text-gray-300 transition hover:bg-[#1F2535]'
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default WeekTimeRangePicker;

WeekTimeRangePicker.propTypes = {
    startTime: PropTypes.string,
    endTime: PropTypes.string,
    onChange: PropTypes.func.isRequired,
};




